import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const KEY = "kaffgum_cart_v2";
const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

function load() {
  try {
    const d = JSON.parse(localStorage.getItem(KEY) || "{}");
    return { items: d.items || {}, lastAdded: d.lastAdded || null };
  } catch {
    return { items: {}, lastAdded: null };
  }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(load);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mergedRef = useRef(false);
  const syncTimer = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
  }, [state]);

  // pull server cart once after login; merge if local is empty
  useEffect(() => {
    if (user === false) {
      // signed out: local cart must not leak into the next session
      if (mergedRef.current) setState({ items: {}, lastAdded: null });
      mergedRef.current = false;
      return;
    }
    if (!user || mergedRef.current) return;
    mergedRef.current = true;
    api.get("/cart").then(({ data }) => {
      setState((s) => {
        if (Object.keys(s.items).length > 0) return s;
        return { items: data.items || {}, lastAdded: data.last_added || s.lastAdded };
      });
    }).catch(() => {});
  }, [user]);

  // debounced push to server
  useEffect(() => {
    if (!user) return;
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      api.put("/cart", { items: state.items, last_added: state.lastAdded }).catch(() => {});
    }, 700);
    return () => clearTimeout(syncTimer.current);
  }, [state, user]);

  const setQty = (id, qty) =>
    setState((s) => {
      const items = { ...s.items };
      if (qty <= 0) delete items[id];
      else items[id] = Math.min(99, qty);
      return { ...s, items };
    });

  const add = (id, qty = 1) =>
    setState((s) => ({
      items: { ...s.items, [id]: Math.min(99, (s.items[id] || 0) + qty) },
      lastAdded: id,
    }));

  const clear = () => setState((s) => ({ ...s, items: {} }));

  const count = useMemo(() => Object.values(state.items).reduce((a, b) => a + b, 0), [state.items]);

  return (
    <CartContext.Provider value={{ items: state.items, lastAdded: state.lastAdded, add, setQty, clear, count, drawerOpen, setDrawerOpen }}>
      {children}
    </CartContext.Provider>
  );
}
